import { useCallback, useEffect, useState } from "react";
import { useGame } from "context/GameContext";
import CombatSystem from "./CombatSystem";
import Action from "./actions/Action";
import * as Styled from "./Event.style";

const Event = ({ event }) => {
  const { gameState, setGameState, addNotification, setEvent } = useGame();
  const { currentEvent = event, eventQueue } = gameState;
  const [description, setDescription] = useState(currentEvent?.description);

  useEffect(() => {
    if (currentEvent || eventQueue.length === 0) return;

    // Display the first event in the queue
    const nextEvent = eventQueue[0];
    setGameState((prev) => ({
      ...prev,
      currentEvent: nextEvent,
      eventQueue: prev.eventQueue.slice(1),
    }));
    setDescription(nextEvent.description);
  }, [currentEvent, eventQueue, setGameState]);

  const handleEsc = useCallback(
    (event) => {
      if (event.keyCode === 27 && currentEvent?.isDismissable) {
        setGameState((prev) => ({ ...prev, currentEvent: null }));
      }
    },
    [setGameState, currentEvent],
  );

  useEffect(() => {
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [handleEsc]);

  if (!currentEvent && !event) return null;

  const { title, buttons, enemies, victory, defeat } = currentEvent || event;

  if (!title) return null;

  return (
    <Styled.Panel title={title} isCombat={enemies}>
      <Styled.Title>{title}</Styled.Title>
      <Styled.Description isCombat={enemies}>
        {description && typeof description === "string"
          ? description
          : description.map((line, index) => {
            // Assuming dialogues are wrapped in square brackets
            const parts = line.split(/(".*?")/).map((part, partIndex) =>
              part.startsWith('"') && part.endsWith('"')
                ? (
                  <span key={partIndex} className="dialogue">
                    {`"${part.slice(1, -1)}"`}
                  </span>
                )
                : part
            );
            return <div key={index}>{parts}</div>;
          })}
      </Styled.Description>
      {enemies && (
        <CombatSystem
          gameState={gameState}
          initialEnemies={enemies}
          victory={victory}
          defeat={defeat}
          onCombatEnd={(description, message) => {
            setDescription(description);
            addNotification(description);
            setGameState((prev) => ({
              ...prev,
              message,
            }));
          }}
          onDone={setEvent}
        />
      )}
      <Styled.Buttons>
        {buttons &&
          buttons.map((btn, i) => (
            <Styled.Button key={i}>
              <Action
                {...btn}
                context={gameState}
                setContext={setGameState}
                onNotification={addNotification}
                event={currentEvent}
                isAccept={buttons.length === 1}
              />
            </Styled.Button>
          ))}
      </Styled.Buttons>
    </Styled.Panel>
  );
};

export default Event;
